import { Button, ConfirmProvider, EmptyState, LoadingState } from '@platform/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
    Link,
    Outlet,
    RouterProvider,
    createRootRoute,
    createRoute,
    createRouter,
    lazyRouteComponent,
    useNavigate,
    useRouter,
    useRouterState,
    type ErrorComponentProps,
} from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import './app.css';
import { AppShell } from './components/AppShell';
import { I18nProvider, useMessages } from './i18n';
import { usePageTitle } from './lib/page-title';
import { SessionProvider, useSession, type Level } from './lib/session';
import { OverviewPage } from './pages/OverviewPage';
import { SignInPage } from './pages/SignInPage';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { refetchOnWindowFocus: false, staleTime: 15_000 },
    },
});

/**
 * The gate.
 *
 * Two conditions, not one: signed in, and platform staff. A seller who reaches
 * this origin with a valid session is shown the sign-in screen with the reason,
 * because their session is perfectly good — it is simply not a session for this
 * application.
 */
function AuthGate({ children }: { children: ReactNode }) {
    const { me, isStaff, isLoading } = useSession();
    const navigate = useNavigate();
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const t = useMessages();

    const signedInAsStaff = Boolean(me) && isStaff;

    useEffect(() => {
        if (isLoading) return;

        if (!signedInAsStaff && pathname !== '/giris') {
            void navigate({ to: '/giris', replace: true });
            return;
        }
        // Staff who are already signed in have no use for the sign-in screen, and
        // leaving them on it after a successful password is how a login appears to
        // do nothing.
        if (signedInAsStaff && pathname === '/giris') {
            void navigate({ to: '/', replace: true });
        }
    }, [isLoading, signedInAsStaff, pathname, navigate]);

    if (isLoading) {
        return <LoadingState label={t.loading} className="min-h-dvh" />;
    }

    if (!signedInAsStaff) {
        return <SignInPage />;
    }

    return <>{children}</>;
}

/** Confirmation dialogs, labelled in the current language. */
function Confirmations({ children }: { children: ReactNode }) {
    const t = useMessages();
    return (
        <ConfirmProvider
            labels={{
                confirm: t.common.confirm,
                cancel: t.common.cancel,
                close: t.common.close,
            }}
        >
            {children}
        </ConfirmProvider>
    );
}

const rootRoute = createRootRoute({
    component: () => (
        <QueryClientProvider client={queryClient}>
            <SessionProvider>
                <Confirmations>
                    <Redirector />
                    <Outlet />
                </Confirmations>
            </SessionProvider>
        </QueryClientProvider>
    ),
});

/**
 * Sends a signed-in staff member off the sign-in screen.
 *
 * Mounted at the root rather than inside the sign-in route, because the route
 * that needs the redirect is the one that renders when there is no session yet,
 * and a component cannot navigate away from itself before it exists.
 */
function Redirector() {
    const { isStaff, isLoading } = useSession();
    const navigate = useNavigate();
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });

    useEffect(() => {
        if (isLoading) return;
        if (isStaff && pathname === '/giris') {
            void navigate({ to: '/', replace: true });
        }
    }, [isLoading, isStaff, pathname, navigate]);

    return null;
}

const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/giris',
    component: SignInPage,
});

const shellRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'shell',
    component: () => (
        <AuthGate>
            <AppShell />
        </AuthGate>
    ),
});

const overviewRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/',
    component: OverviewPage,
});

const accountsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/hesaplar',
    component: lazyRouteComponent(() => import('./pages/AccountsPage'), 'AccountsPage'),
});

// Declared before the parameterised route so "/hesaplar" is matched exactly and
// only a real id falls through to the detail view.
const accountDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/hesaplar/$tenantId',
    component: lazyRouteComponent(() => import('./pages/AccountDetailPage'), 'AccountDetailPage'),
});

// The pages above support name the level they need, and the shell refuses them
// to anyone below it (CLAUDE.md §8.2): the level the API demands of the page's
// own data, and for announcements the level that may publish one.
const revenueRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/gelir',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/RevenuePage'), 'RevenuePage'),
});

const announcementsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/duyurular',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/AnnouncementsPage'), 'AnnouncementsPage'),
});

const staffRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/personel',
    staticData: { level: 'superadmin' },
    component: lazyRouteComponent(() => import('./pages/StaffPage'), 'StaffPage'),
});

const usersRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kullanicilar',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/UsersPage'), 'UsersPage'),
});

const auditRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/denetim',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/AuditPage'), 'AuditPage'),
});

const systemRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/sistem',
    component: lazyRouteComponent(() => import('./pages/SystemPage'), 'SystemPage'),
});

const plansRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/planlar',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/PlansPage'), 'PlansPage'),
});

// Approving a transfer and reading where transfers go are admin work; the
// refund and the bank account itself are the superadmin's, decided per action.
const paymentsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/odemeler',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/PaymentsPage'), 'PaymentsPage'),
});

const paymentSettingsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/odeme-ayarlari',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/PaymentSettingsPage'), 'PaymentSettingsPage'),
});

// Reading the issuer and checking a connection are admin work; changing either
// is the superadmin's, decided per action.
const invoiceSettingsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/fatura-ayarlari',
    staticData: { level: 'admin' },
    component: lazyRouteComponent(() => import('./pages/InvoiceSettingsPage'), 'InvoiceSettingsPage'),
});

const requestsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/talepler',
    component: lazyRouteComponent(() => import('./pages/RequestsPage'), 'RequestsPage'),
});

// The operator's own sign-in, two-step verification among it; every staff
// level has one.
const accountRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/hesabim',
    component: lazyRouteComponent(() => import('./pages/AccountPage'), 'AccountPage'),
});

const routeTree = rootRoute.addChildren([
    signInRoute,
    shellRoute.addChildren([
        overviewRoute,
        accountsRoute,
        accountDetailRoute,
        revenueRoute,
        announcementsRoute,
        staffRoute,
        usersRoute,
        auditRoute,
        systemRoute,
        plansRoute,
        paymentsRoute,
        paymentSettingsRoute,
        invoiceSettingsRoute,
        requestsRoute,
        accountRoute,
    ]),
]);

/** An address that matches no page: say so, and give the way back. */
function NotFound() {
    const t = useMessages();
    usePageTitle(t.common.notFound.title);
    return (
        <div className="grid min-h-dvh place-items-center">
            <EmptyState
                titleAs="h1"
                title={t.common.notFound.title}
                description={t.common.notFound.body}
                action={
                    <Button asChild variant="secondary">
                        <Link to="/">{t.common.notFound.home}</Link>
                    </Button>
                }
            />
        </div>
    );
}

/**
 * What the browsers say when a page's file did not arrive: the three messages
 * the router itself reloads on once, a deploy having replaced the file.
 */
const CHUNK_FAILURE = /(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/;

/**
 * Stands in while a page's code is fetched (ADR 0004 §1). The router shows it
 * only after its pending delay, so a fast load does not flash.
 */
function PagePending() {
    const t = useMessages();
    return <LoadingState label={t.loading} />;
}

/**
 * Stands in for a page that failed to draw (docs/11 §8). It words the failure and
 * never shows the error — the console has the message, and a seller can do
 * nothing with it.
 */
function PageError({ error, reset }: ErrorComponentProps) {
    const t = useMessages();
    const router = useRouter();
    usePageTitle(t.common.error.title);

    function retry() {
        // The router keeps a page's failed import and throws it again on every
        // draw, so drawing the route once more cannot fetch the file; only a
        // reload does (ADR 0004 §1).
        if (CHUNK_FAILURE.test(String(error))) {
            window.location.reload();
            return;
        }
        // The cache may hold the very answer the page could not draw, and a page
        // mounted over it fails the same way before it can ask again. Dropping
        // what no screen is watching makes the retry ask afresh.
        queryClient.removeQueries({ type: 'inactive' });
        void router.invalidate();
        reset();
    }

    return (
        <EmptyState
            titleAs="h1"
            icon={<TriangleAlert aria-hidden="true" />}
            title={t.common.error.title}
            description={t.common.error.body}
            action={
                <>
                    <Button onClick={retry}>{t.common.error.retry}</Button>
                    <Button asChild variant="secondary">
                        <Link to="/">{t.common.error.home}</Link>
                    </Button>
                </>
            }
        />
    );
}

const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultPendingComponent: PagePending,
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: PageError,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
    interface StaticDataRouteOption {
        /** The staff level a page needs; a page without one is open to all staff. */
        level?: Level;
    }
}

const container = document.getElementById('root');
if (!container) {
    throw new Error('#root is missing from index.html');
}

// The dictionary wraps the router rather than sitting in the root route, so the
// error screen can word itself even when the root's own providers are what
// failed (docs/11 §8).
createRoot(container).render(
    <StrictMode>
        <I18nProvider>
            <RouterProvider router={router} />
        </I18nProvider>
    </StrictMode>,
);
